import React from 'react';
import { motion } from 'framer-motion';
import { Play, Radio, Tv, Users, Zap, Shield, MessageSquare, Share2, DollarSign, ArrowRight, CheckCircle2, Video, Mic, Cast } from 'lucide-react';
export function Streaming() {
  return <div className="min-h-screen bg-[#0D0D0D] text-white" data-id="element-475">
      <div className="pt-48 pb-32 px-6 max-w-7xl mx-auto" data-id="element-476">
        {/* Hero Section */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-32" data-id="element-477">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} className="space-y-8" data-id="element-478">
            <span className="text-[#FF0000] font-bold uppercase tracking-[0.3em] text-xs" data-id="element-479">
              Live & Uncut
            </span>
            <h2 className="font-['Archivo_Black'] text-4xl md:text-6xl uppercase leading-tight" data-id="element-480">
              Own the <span className="text-[#FF0000]" data-id="element-481">Broadcast</span>
            </h2>
            <p className="text-xl text-gray-400 leading-relaxed" data-id="element-482">
              Broadcast directly to your fans without platform censorship,
              algorithm filters, or massive revenue cuts. This is your stage.
            </p>
            <div className="space-y-6" data-id="element-483">
              {[{
              title: '4K Ultra HD',
              desc: 'Crystal clear quality for your performances.',
              icon: Play
            }, {
              title: 'Low Latency',
              desc: 'Real-time interaction with your audience.',
              icon: Radio
            }].map((feat, i) => <div key={i} className="flex gap-4 group" data-id="element-484">
                  <div className="w-12 h-12 bg-[#FF0000]/10 rounded-lg flex items-center justify-center shrink-0 group-hover:bg-[#FF0000]/20 transition-colors" data-id="element-485">
                    <feat.icon className="text-[#FF0000]" size={24} data-id="element-486" />
                  </div>
                  <div data-id="element-487">
                    <h4 className="font-bold uppercase tracking-wider" data-id="element-488">
                      {feat.title}
                    </h4>
                    <p className="text-gray-500 text-sm" data-id="element-489">{feat.desc}</p>
                  </div>
                </div>)}
            </div>
            <button className="bg-[#FF0000] text-white px-10 py-5 rounded-full font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-all shadow-[0_0_30px_rgba(255,0,0,0.3)] flex items-center gap-3" data-id="element-490">
              Start Streaming Now <ArrowRight size={18} data-id="element-491" />
            </button>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="aspect-video bg-[#1A1A1A] rounded-3xl border border-white/10 overflow-hidden relative group shadow-2xl" data-id="element-492">
            <img src="https://images.unsplash.com/photo-1516280440614-37939bbdd4f1?auto=format&fit=crop&q=80&w=1200" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:scale-105 transition-transform duration-700" alt="Live Stream Preview" data-id="element-493" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" data-id="element-494" />
            <div className="absolute inset-0 flex items-center justify-center" data-id="element-495">
              <div className="w-20 h-20 bg-[#FF0000] rounded-full flex items-center justify-center shadow-[0_0_50px_rgba(255,0,0,0.4)] group-hover:scale-110 transition-transform cursor-pointer" data-id="element-496">
                <Play className="text-white ml-1" size={32} data-id="element-497" />
              </div>
            </div>
            <div className="absolute top-6 left-6 flex items-center gap-2 bg-black/60 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/10" data-id="element-498">
              <div className="w-2 h-2 bg-[#FF0000] rounded-full animate-pulse" data-id="element-499"></div>
              <span className="text-[10px] font-bold uppercase tracking-widest" data-id="element-500">
                Live Preview
              </span>
            </div>
            <div className="absolute bottom-6 left-6 flex items-center gap-4" data-id="element-501">
              <div className="flex -space-x-2" data-id="element-502">
                {[1, 2, 3].map(i => <div key={i} className="w-8 h-8 rounded-full border-2 border-black bg-gray-800 overflow-hidden" data-id="element-503">
                    <img src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="viewer" data-id="element-504" />
                  </div>)}
              </div>
              <span className="text-xs font-bold text-white/80" data-id="element-505">
                12.4k watching
              </span>
            </div>
          </motion.div>
        </section>

        {/* Monetization Section */}
        <section className="mb-32" data-id="element-506">
          <div className="text-center mb-16" data-id="element-507">
            <h2 className="font-['Archivo_Black'] text-3xl md:text-5xl uppercase mb-4" data-id="element-508">
              Monetize Your Way
            </h2>
            <p className="text-gray-500 uppercase font-bold tracking-widest" data-id="element-509">
              Keep 100% of your revenue. No platform tax.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-510">
            {[{
            title: 'Ticketed Events',
            desc: 'Sell digital tickets for exclusive virtual concerts or meet-and-greets.',
            icon: DollarSign,
            color: 'text-emerald-500'
          }, {
            title: 'Fan Subscriptions',
            desc: 'Build a recurring revenue stream with private, members-only streams.',
            icon: Users,
            color: 'text-blue-500'
          }, {
            title: 'Direct Tips',
            desc: 'Integrated tipping system that goes straight to your account.',
            icon: Zap,
            color: 'text-yellow-500'
          }].map((item, i) => <div key={i} className="p-10 bg-[#1A1A1A] rounded-3xl border border-white/5 hover:border-white/20 transition-all group" data-id="element-511">
                <item.icon className={`${item.color} mb-6 group-hover:scale-110 transition-transform`} size={32} data-id="element-512" />
                <h3 className="font-['Archivo_Black'] text-xl uppercase mb-4" data-id="element-513">
                  {item.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed" data-id="element-514">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </section>

        {/* Interactive Features */}
        <section className="mb-32 p-12 bg-gradient-to-br from-[#1A1A1A] to-[#0D0D0D] border border-white/10 rounded-[3rem] relative overflow-hidden" data-id="element-515">
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-516">
            <div className="space-y-8" data-id="element-517">
              <h2 className="font-['Archivo_Black'] text-3xl md:text-5xl uppercase leading-tight" data-id="element-518">
                Engage Your <br data-id="element-519" />
                <span className="text-[#FF0000]" data-id="element-520">Community</span>
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed" data-id="element-521">
                Our streaming suite includes professional-grade engagement tools
                designed to turn viewers into superfans.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8" data-id="element-522">
                {[{
                title: 'Real-time Chat',
                icon: MessageSquare,
                desc: 'Ultra-fast moderated chat'
              }, {
                title: 'Social Sharing',
                icon: Share2,
                desc: 'Viral loops built-in'
              }, {
                title: 'Polls & Q&A',
                icon: CheckCircle2,
                desc: 'Interactive live segments'
              }, {
                title: 'Multi-Cast',
                icon: Cast,
                desc: 'Stream to all platforms'
              }].map((feat, i) => <div key={i} className="space-y-2" data-id="element-523">
                    <div className="flex items-center gap-3" data-id="element-524">
                      <feat.icon className="text-[#FF0000]" size={20} data-id="element-525" />
                      <h4 className="font-bold uppercase text-sm tracking-wider" data-id="element-526">
                        {feat.title}
                      </h4>
                    </div>
                    <p className="text-xs text-gray-500" data-id="element-527">{feat.desc}</p>
                  </div>)}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-528">
              <div className="space-y-4" data-id="element-529">
                <div className="aspect-[4/5] bg-white/5 rounded-2xl border border-white/10 overflow-hidden relative group" data-id="element-530">
                  <img src="https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?auto=format&fit=crop&q=80&w=600" className="w-full h-full object-cover opacity-50 group-hover:scale-110 transition-transform duration-700" alt="Studio" data-id="element-531" />
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-532">
                    <Video className="text-white/20" size={48} data-id="element-533" />
                  </div>
                </div>
                <div className="aspect-square bg-[#FF0000]/10 rounded-2xl border border-[#FF0000]/20 flex items-center justify-center" data-id="element-534">
                  <Mic className="text-[#FF0000]" size={48} data-id="element-535" />
                </div>
              </div>
              <div className="space-y-4 pt-8" data-id="element-536">
                <div className="aspect-square bg-white/5 rounded-2xl border border-white/10 flex items-center justify-center" data-id="element-537">
                  <Tv className="text-white/20" size={48} data-id="element-538" />
                </div>
                <div className="aspect-[4/5] bg-white/5 rounded-2xl border border-white/10 overflow-hidden relative group" data-id="element-539">
                  <img src="https://images.unsplash.com/photo-1470225620780-dba8ba36b745?auto=format&fit=crop&q=80&w=600" className="w-full h-full object-cover opacity-50 group-hover:scale-110 transition-transform duration-700" alt="DJ" data-id="element-540" />
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-541">
                    <Users className="text-white/20" size={48} data-id="element-542" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-[#FF0000]/5 blur-[120px] rounded-full" data-id="element-543" />
        </section>

        {/* Technical Specs */}
        <section className="mb-32" data-id="element-544">
          <div className="bg-[#1A1A1A] border border-white/5 rounded-3xl p-8 md:p-16" data-id="element-545">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-546">
              <div className="lg:col-span-1 space-y-6" data-id="element-547">
                <h3 className="font-['Archivo_Black'] text-2xl uppercase" data-id="element-548">
                  The Tech Behind the Stream
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed" data-id="element-549">
                  We use Wowza-powered enterprise infrastructure to ensure your
                  broadcast is stable, secure, and high-fidelity.
                </p>
                <div className="flex items-center gap-4 p-4 bg-black/40 rounded-xl border border-white/5" data-id="element-550">
                  <Shield className="text-emerald-500" size={24} data-id="element-551" />
                  <div data-id="element-552">
                    <p className="text-xs font-bold uppercase text-emerald-500" data-id="element-553">
                      Encrypted Stream
                    </p>
                    <p className="text-[10px] text-gray-500" data-id="element-554">
                      AES-128 Protection
                    </p>
                  </div>
                </div>
              </div>
              <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-3 gap-8" data-id="element-555">
                {[{
                label: 'Resolution',
                value: 'Up to 4K'
              }, {
                label: 'Bitrate',
                value: '20 Mbps'
              }, {
                label: 'Protocol',
                value: 'SRT / RTMP'
              }, {
                label: 'CDN',
                value: 'Global Edge'
              }, {
                label: 'Storage',
                value: 'Unlimited VOD'
              }, {
                label: 'Backup',
                value: 'Dual-Ingest'
              }].map((spec, i) => <div key={i} className="space-y-1" data-id="element-556">
                    <p className="text-[10px] font-bold uppercase text-gray-500 tracking-widest" data-id="element-557">
                      {spec.label}
                    </p>
                    <p className="text-lg font-bold text-white" data-id="element-558">{spec.value}</p>
                  </div>)}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center py-20 border-t border-white/5" data-id="element-559">
          <h3 className="font-['Archivo_Black'] text-2xl md:text-4xl uppercase mb-8" data-id="element-560">
            Ready to go live?
          </h3>
          <p className="text-gray-400 mb-12 max-w-2xl mx-auto" data-id="element-561">
            Join the elite creators who broadcast on their own terms. No limits.
            No censorship. Just your art.
          </p>
          <button className="group flex items-center gap-3 bg-white text-black px-12 py-6 rounded-full font-bold uppercase tracking-widest hover:bg-[#FF0000] hover:text-white transition-all shadow-xl mx-auto" data-id="element-562">
            Get Streaming Access
            <ArrowRight className="group-hover:translate-x-2 transition-transform" data-id="element-563" />
          </button>
        </section>
      </div>
    </div>;
}